/* WeQuote Dev Board — My Board view (v2 §4, verify §72/§73). Per-login by
 * default: dev-only logins land on their own board with no switcher;
 * Natalie + managers get a "Viewing as [dev]" switcher (locked decision 3).
 * Sections: action tiles · Working on now (hold + complete tick, click to
 * open/add progress) · Next – same day · Rest of this week · Up next
 * (Now + Next bands) · Completed. Live look throughout. */
import React, { useState } from 'react'
import { useDevBoard } from '../DevBoardProvider'
import { useAuth } from '../../../../contexts/AuthContext'
import * as MB from '../core/myboard'
import * as M from '../core/mutations'
import * as ST from '../core/stages'
import type { Feature } from '../core/types'
import { DEVCOLS, DL, SLGet, timeLabel } from '../core/constants'
import { fmtDate, todayISO } from '../core/dates'
import * as OV from '../core/overview'
import { devColor } from '../core/constants'
import { cardStatus } from '../core/logic'
import { pastDesign } from '../core/constants'
import { TitleWithBug } from './parts'
import { IcPause, IcPlay, IcCheck } from '../icons'

export const MyBoardView: React.FC = () => {
  const { cards, mutate, openCardModal, sched } = useDevBoard()
  const { currentAgent } = useAuth()
  const devOnly = !!(currentAgent as any)?.devOnly
  const email: string = (currentAgent as any)?.email || ''
  const mySlug = email.indexOf('@') > 0 ? email.split('@')[0].toLowerCase() : ''
  const defaultDev = DEVCOLS.indexOf(mySlug) > -1 ? mySlug : DEVCOLS[0] || ''
  const [dev, setDev] = useState(defaultDev)
  const nowD = new Date()
  const [cYear, setCYear] = useState(nowD.getFullYear())
  const [cMonth, setCMonth] = useState<OV.OvMonth>(nowD.getMonth() as OV.OvMonth)
  const board = MB.buildMyBoard(cards, dev, sched.start, todayISO())
  const myCards = cards.filter((f) => MB.isMine(f, dev))
  const doneP = OV.completedInPeriod(myCards, cYear, cMonth)
  const reqsP = OV.requestsInPeriod(myCards, cYear, cMonth)
  const years = Array.from({ length: 3 }, (_, i) => nowD.getFullYear() - 1 + i)

  const mdComplete = (f: Feature) => {
    if (!window.confirm('Mark “' + f.title + '” complete and move to Ready to Test?')) return
    mutate((fs) => M.moveStatus(fs, f.id, 'testing'))
  }
  const stageTick = (u: MB.StageRef) => {
    mutate((fs) => ST.markStageDone(fs, u.f.id, u.s.id))
    const after = { ...u.f, stages: u.f.stages.map((x) => (x.id === u.s.id ? { ...x, done: true } : x)) }
    if (ST.allStagesDone(after) && ['testing', 'testcomplete', 'live'].indexOf(u.f.status) < 0) {
      setTimeout(() => {
        if (window.confirm('All stages of “' + u.f.title + '” are complete.\n\nMove it to Ready to Test?')) {
          mutate((fs) => M.moveStatus(fs, u.f.id, 'testing'))
        }
      }, 60)
    }
  }
  const NowRow: React.FC<{ u: MB.MyUnit }> = ({ u }) => {
    const f = u.f
    if (u.stage) {
      const s = u.stage.s
      return (
        <div className="md-card md-stagecard">
          <div className="md-card-top">
            <span className="md-card-t" onClick={() => openCardModal(f.id)}>{f.title} · Stage {u.stage.i + 1}{s.name ? ': ' + s.name : ''}</span>
            <span className="md-actions"><button className="md-tick" title="Mark this stage complete" onClick={(e) => { e.stopPropagation(); stageTick(u.stage!) }}><IcCheck size={12} /></button></span>
          </div>
          <div className="md-card-sub" onClick={() => openCardModal(f.id)}>{s.estCompletion ? 'finishes ' + fmtDate(s.estCompletion) : ''}{s.time ? ' · ' + timeLabel(s.time) : ''} · <span className="md-add">open card</span></div>
        </div>
      )
    }
    return (
      <div className="md-card">
        <div className="md-card-top">
          <span className="md-card-t" onClick={() => openCardModal(f.id)}><TitleWithBug feat={f} /></span>
          <span className="md-actions">
            {f.status === 'onhold'
              ? <button className="md-hold on" title="Resume" onClick={(e) => { e.stopPropagation(); mutate((fs) => M.resumeCard(fs, f.id)) }}><IcPlay size={12} /></button>
              : pastDesign(f.status)
                ? <button className="md-hold" title="Place on hold" onClick={(e) => { e.stopPropagation(); mutate((fs) => M.placeOnHold(fs, f.id)) }}><IcPause size={12} /></button>
                : null}
            <button className="md-tick" title="Mark complete → Ready to Test" onClick={(e) => { e.stopPropagation(); mdComplete(f) }}><IcCheck size={12} /></button>
          </span>
        </div>
        <div className="pbar"><div className={'pbar-fill' + (f.status === 'onhold' ? ' hold' : '')} style={{ width: (f.progress || 0) + '%' }} /></div>
        <div className="md-card-sub" onClick={() => openCardModal(f.id)}>{f.progress || 0}% · {SLGet(f.status)}{f.deadline ? ' · due ' + fmtDate(f.deadline) : ''} · <span className="md-add">add progress</span></div>
      </div>
    )
  }
  const SchedRow: React.FC<{ u: MB.MyUnit }> = ({ u }) => {
    const lbl = u.stage ? u.f.title + ' · Stage ' + (u.stage.i + 1) + (u.stage.s.name ? ': ' + u.stage.s.name : '') : u.f.title
    const ends = u.stage ? u.stage.s.estCompletion : u.f.estCompletion
    return (
      <div className="md-lrow" onClick={() => openCardModal(u.f.id)}>
        <span className="md-lrow-t">{lbl}</span>
        <span className="md-lrow-d">starts {fmtDate(u.sStart!)}{ends ? ' · ends ' + fmtDate(ends) : ''}</span>
      </div>
    )
  }
  const Sec: React.FC<{ title: string; count: number; children: React.ReactNode }> = ({ title, count, children }) => (
    <div className="ovc md-sec">
      <div className="ovc-hd"><h3>{title}</h3><span className="md-sec-n">{count}</span></div>
      <div className="md-sec-body">{children}</div>
    </div>
  )
  const Tile: React.FC<{ label: string; n: number; cls: string }> = ({ label, n, cls }) => (
    <div className={'md-tile' + (n > 0 ? ' ' + cls : '')}><div className="md-tile-n">{n}</div><div className="md-tile-l">{label}</div></div>
  )

  return (
    <div id="myDevView">
      <div className="md-wrap">
        {!devOnly && (
          <div className="md-switch">
            <span>Viewing as</span>
            <select value={dev} onChange={(e) => setDev(e.target.value)}>
              {DEVCOLS.map((d) => <option key={d} value={d}>{DL[d]}</option>)}
            </select>
          </div>
        )}
        <div className="md-tiles">
          <Tile label="Design overdue" n={board.tiles.designOverdue} cls="red" />
          <Tile label="Dev overdue" n={board.tiles.devOverdue} cls="red" />
          <Tile label="At risk" n={board.tiles.atRisk} cls="amber" />
          <Tile label="Design due soon" n={board.tiles.designSoon} cls="amber" />
        </div>
        <div className="md-cols">
          <div className="md-col">
            <Sec title="Working on now" count={board.nowItems.length}>
              {board.nowItems.length ? <div className="md-cards">{board.nowItems.map((u, i) => <NowRow key={u.f.id + ':' + (u.stage ? u.stage.i : 'c') + i} u={u} />)}</div> : <div className="md-empty">Nothing in progress right now.</div>}
            </Sec>
            <Sec title="Next – same day" count={board.nextToday.length}>
              {board.nextToday.length ? <div className="md-list">{board.nextToday.map((u, i) => <SchedRow key={'t' + u.f.id + i} u={u} />)}</div> : <div className="md-empty">Nothing else scheduled today.</div>}
            </Sec>
            <Sec title="Rest of this week" count={board.thisWeek.length}>
              {board.thisWeek.length ? <div className="md-list">{board.thisWeek.map((u, i) => <SchedRow key={'w' + u.f.id + i} u={u} />)}</div> : <div className="md-empty">Nothing scheduled the rest of this week.</div>}
            </Sec>
          </div>
          <div className="md-col">
            <div className="ovc md-completed">
              <div className="ovc-hd">
                <h3>Completed</h3>
                <div className="ovc-tools">
                  <select value={cMonth === 'all' ? 'all' : String(cMonth)} onChange={(e) => setCMonth(e.target.value === 'all' ? 'all' : parseInt(e.target.value, 10) as OV.OvMonth)}>
                    <option value="all">All months</option>
                    {OV.OV_MONTHS.map((m, i) => <option key={i} value={i}>{m}</option>)}
                  </select>
                  <select value={cYear} onChange={(e) => setCYear(parseInt(e.target.value, 10))}>
                    {years.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>
              <div className="ovx-ana-body">
                {doneP.length ? (
                  <svg viewBox="0 0 120 120" width={118} height={118} className="ovx-pie">
                    <circle cx={60} cy={60} r={54} fill={devColor(dev)} />
                    <circle cx={60} cy={60} r={32} className="ovx-hole" fill="#fff" />
                    <text x={60} y={59} textAnchor="middle" fontSize={22} fontWeight={700} fill="#111827">{doneP.length}</text>
                    <text x={60} y={75} textAnchor="middle" fontSize={9} fill="#9ca3af">done</text>
                  </svg>
                ) : (
                  <div className="ovx-empty">Nothing completed in this period.</div>
                )}
                {doneP.length > 0 && (
                  <div className="ovx-legend md-done-list">
                    {doneP.slice(0, 12).map((f) => (
                      <div key={f.id} className="md-lrow" onClick={() => openCardModal(f.id)}>
                        <span className="md-lrow-t">{f.title}</span>
                        <span className="md-lrow-d">{f.completedOn ? fmtDate(f.completedOn) : 'live'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="ovx-ana-foot">
                <div><div className="ovx-stat-n">{doneP.length}</div><div className="ovx-stat-l">Completed</div></div>
                <div><div className="ovx-stat-n">{reqsP.length}</div><div className="ovx-stat-l">Requests added</div></div>
              </div>
            </div>
            <Sec title="Up next" count={board.upnext.length}>
              {board.upnext.length ? <div className="md-list">{board.upnext.map((f) => (
                <div key={f.id} className="md-lrow" onClick={() => openCardModal(f.id)}>
                  <span className="md-lrow-t"><TitleWithBug feat={f} /></span>
                  <span className={'md-lrow-b band-' + f.status}>{f.status === 'now' ? 'Now' : f.status === 'next' ? 'Next' : SLGet(f.status)}</span>
                </div>
              ))}</div> : <div className="md-empty">Nothing queued in Now / Next.</div>}
            </Sec>
          </div>
        </div>
      </div>
    </div>
  )
}
